import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ClipboardCheck, Eye, Target, Briefcase, ArrowRight, CheckCircle2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { PageHero, Section, SectionHeading } from "@/components/blocks";
import BuyButton from "@/components/BuyButton";
import api from "@/lib/api";

const STAGES = [
  { icon: ClipboardCheck, title: "Before Placement", items: ["Placement expectations", "Professional conduct", "Building mentor relationships", "Observation strategies"], color: "bg-teal-600" },
  { icon: Eye, title: "During Placement", items: ["Reflection resources", "Weekly guidance", "Goal setting", "Classroom readiness"], color: "bg-emerald-600" },
  { icon: Briefcase, title: "After Placement", items: ["Employment preparation", "CV development", "Interview preparation", "Professional growth"], color: "bg-slate-800" },
];

const PRODUCT_IDS = ["r5", "r7"];
const PRODUCT_LABELS = { r5: "Placement Journal", r7: "Placement Ready Toolkit" };

export default function FutureTeachers() {
  const [resources, setResources] = useState([]);
  useEffect(() => { api.get("/resources").then((r) => setResources(r.data)).catch(() => {}); }, []);
  const products = PRODUCT_IDS.map((id) => resources.find((r) => r.id === id)).filter(Boolean);

  return (
    <div data-testid="future-teachers-page">
      <PageHero
        eyebrow="Future Teachers"
        title="Walk into placement fully prepared."
        subtitle="Placement preparation, training, resources and employability support for pre-service teachers at every stage of the journey."
        image="https://images.pexels.com/photos/5940836/pexels-photo-5940836.jpeg?auto=compress&cs=tinysrgb&w=1600"
      >
        <Button asChild size="lg" className="bg-teal-600 hover:bg-teal-700 rounded-full px-6"><Link to="/resources" data-testid="ft-hero-cta">Explore resources <ArrowRight className="w-4 h-4" /></Link></Button>
      </PageHero>

      <Section>
        <SectionHeading eyebrow="Your journey" title="Support at every stage of placement" center />
        <div className="grid md:grid-cols-3 gap-6">
          {STAGES.map((s) => (
            <Card key={s.title} className="p-8 border-slate-200" data-testid={`ft-stage-${s.title}`}>
              <div className={`w-12 h-12 rounded-xl ${s.color} flex items-center justify-center mb-5`}><s.icon className="w-6 h-6 text-white" /></div>
              <h3 className="font-display text-xl font-bold text-slate-900 mb-4">{s.title}</h3>
              <ul className="space-y-3">
                {s.items.map((it) => (
                  <li key={it} className="flex items-start gap-2 text-slate-600"><CheckCircle2 className="w-4 h-4 text-emerald-500 mt-1 shrink-0" /> {it}</li>
                ))}
              </ul>
            </Card>
          ))}
        </div>
      </Section>

      <div className="bg-slate-50 border-y border-slate-200">
        <Section>
          <SectionHeading eyebrow="Featured products" title="Everything you need to succeed on prac" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-3xl">
            {products.map((p) => (
              <Card key={p.id} className="p-6 flex flex-col border-slate-200" data-testid={`ft-product-${p.id}`}>
                <Badge variant="secondary" className="w-fit mb-3">{p.format}</Badge>
                <h3 className="font-display font-bold text-slate-900">{PRODUCT_LABELS[p.id] || p.title}</h3>
                <p className="text-sm text-slate-600 mt-2 flex-1">{p.description}</p>
                <div className="text-xl font-bold text-slate-900 my-4">${p.price}</div>
                <BuyButton itemId={p.id} itemName={p.title} price={p.price} className="w-full bg-slate-900 hover:bg-teal-700 rounded-full" testId={`ft-buy-${p.id}`} />
              </Card>
            ))}
          </div>
        </Section>
      </div>
    </div>
  );
}
